import { useMemo, useState } from "react";
import { useApiUrl, useCustomMutation, useList, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SearchIcon,
  SectionHeader,
  SelectField,
  StatusBadge,
  type Column,
  type SelectOption,
} from "@fawkes/ui";

import { getAccessToken } from "../../auth";
import "./GrootboekList.css";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// settings-grootboek: the React port of the Jmix `SettingsDashboardView` finance-ledger card
// (`buildFinanceLedgerCard` + the create/edit/delete dialogs). Wired from the Financieel tile of the
// settings dashboard. CRUD over `/api/grootboek-rekeningen`; delete routes through a dialog that
// surfaces reference usage + a replacement selector (the reassign-on-delete the backend performs).

export interface GrootboekRekening {
  id: number;
  rekeningNummer: string;
  omschrijving: string;
  rekeningType: string;
  actief: boolean;
}

// Canonical ledger-type tokens — stored verbatim (Dutch) in `grootboek_rekening.rekening_type`, so
// the option *value* stays the token; only the label is localised (`instellingen.grootboek.type.*`).
const LEDGER_TYPES = ["Activa", "Passiva", "Kosten", "Opbrengst"] as const;

interface EditState {
  id: number | null;
  rekeningNummer: string;
  omschrijving: string;
  rekeningType: string;
  actief: boolean;
}
interface DeleteState {
  row: GrootboekRekening;
  usage: number; // total blocking references; 0 = delete straight away
  replacementId: string;
}

const RESOURCE = "grootboek-rekeningen";

export function GrootboekList({ onBack }: { onBack?: () => void } = {}) {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const { data, isLoading, isError, refetch } = useList<GrootboekRekening>({ resource: RESOURCE });
  const { mutate: mutateCustom, isLoading: isSaving } = useCustomMutation();

  const [query, setQuery] = useState("");
  const [edit, setEdit] = useState<EditState | null>(null);
  const [del, setDel] = useState<DeleteState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const all = useMemo(() => data?.data ?? [], [data]);
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (g) =>
        g.rekeningNummer.toLowerCase().includes(needle) ||
        g.omschrijving.toLowerCase().includes(needle),
    );
  }, [all, query]);

  function typeLabel(value: string): string {
    const key = `instellingen.grootboek.type.${value.toLowerCase()}`;
    const label = t(key);
    return label === key ? value : label;
  }

  function startCreate() {
    setError(null);
    setEdit({ id: null, rekeningNummer: "", omschrijving: "", rekeningType: "Kosten", actief: true });
  }
  function startEdit(row: GrootboekRekening) {
    setError(null);
    setEdit({
      id: row.id,
      rekeningNummer: row.rekeningNummer,
      omschrijving: row.omschrijving,
      rekeningType: LEDGER_TYPES.includes(row.rekeningType as (typeof LEDGER_TYPES)[number])
        ? row.rekeningType
        : "Kosten",
      actief: row.actief,
    });
  }

  const isEditValid =
    edit != null &&
    edit.rekeningNummer.trim() !== "" &&
    edit.omschrijving.trim() !== "" &&
    edit.rekeningType.trim() !== "";

  function save() {
    if (!edit || !isEditValid) return;
    const values = {
      rekeningNummer: edit.rekeningNummer.trim(),
      omschrijving: edit.omschrijving.trim(),
      rekeningType: edit.rekeningType,
      actief: edit.actief,
    };
    // Relative path: the dataProvider's `custom` prepends the /api base (see dataProvider.ts).
    mutateCustom(
      {
        url: edit.id == null ? RESOURCE : `${RESOURCE}/${edit.id}`,
        method: edit.id == null ? "post" : "put",
        values,
      },
      {
        onSuccess: () => {
          setEdit(null);
          refetch();
        },
        onError: () => setError(t("instellingen.grootboek.saveFailed")),
      },
    );
  }

  // Resolve reference usage, then either delete straight away or open the reassign dialog.
  async function askDelete(row: GrootboekRekening) {
    setError(null);
    let usage = 0;
    try {
      // Raw fetch (not via the dataProvider), so it DOES need the apiUrl base prepended.
      const res = await fetch(`${apiUrl}/${RESOURCE}/${row.id}/usage`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${getAccessToken() ?? ""}` },
      });
      if (res.ok) usage = (await res.json()).total ?? 0;
    } catch {
      usage = 0;
    }
    setEdit(null);
    if (usage === 0) {
      doDelete(row.id, null);
    } else {
      setDel({ row, usage, replacementId: "" });
    }
  }

  function doDelete(id: number, replacementId: string | null) {
    const qs = replacementId ? `?replacementId=${replacementId}` : "";
    mutateCustom(
      { url: `${RESOURCE}/${id}${qs}`, method: "delete", values: {} },
      {
        onSuccess: () => {
          setDel(null);
          refetch();
        },
        onError: () => setError(t("instellingen.grootboek.deleteFailed")),
      },
    );
  }

  const columns: Column<GrootboekRekening>[] = [
    { header: t("instellingen.grootboek.column.nummer"), cell: (g) => g.rekeningNummer },
    { header: t("instellingen.grootboek.column.omschrijving"), cell: (g) => g.omschrijving },
    { header: t("instellingen.grootboek.column.type"), cell: (g) => typeLabel(g.rekeningType) },
    {
      header: t("instellingen.grootboek.column.status"),
      cell: (g) =>
        g.actief ? (
          <StatusBadge tone="success">{t("instellingen.grootboek.status.active")}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{t("instellingen.grootboek.status.inactive")}</StatusBadge>
        ),
    },
    {
      header: t("instellingen.grootboek.column.actions"),
      cell: (g) => (
        <div className="grootboek-row-actions">
          <Button variant="secondary" onClick={() => startEdit(g)}>
            {t("instellingen.grootboek.edit")}
          </Button>
          <Button variant="secondary" onClick={() => askDelete(g)} disabled={isSaving}>
            {t("instellingen.grootboek.delete")}
          </Button>
        </div>
      ),
    },
  ];

  const typeOptions: SelectOption[] = LEDGER_TYPES.map((value) => ({ value, label: typeLabel(value) }));
  const replacementOptions: SelectOption[] = del
    ? [
        { value: "", label: t("instellingen.grootboek.delete.choosePlaceholder") },
        ...all
          .filter((g) => g.id !== del.row.id)
          .map((g) => ({ value: String(g.id), label: `${g.rekeningNummer} — ${g.omschrijving}` })),
      ]
    : [];

  return (
    <div className="grootboek-view">
      <div className="grootboek-header">
        {onBack && (
          <Button variant="secondary" onClick={onBack}>
            {t("common.back")}
          </Button>
        )}
        <SectionHeader>{t("instellingen.grootboek.title")}</SectionHeader>
      </div>

      <div className="grootboek-toolbar">
        <div className="grootboek-search">
          <SearchIcon className="grootboek-search__icon" width={16} height={16} />
          <input
            type="search"
            autoComplete="off"
            className="grootboek-search__input"
            placeholder={t("instellingen.grootboek.search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("instellingen.grootboek.search.placeholder")}
          />
        </div>
        <div className="grootboek-toolbar__spacer" />
        <Button variant="primary" onClick={startCreate}>
          <PlusCircleSolidIcon width={18} height={18} /> {t("instellingen.grootboek.create")}
        </Button>
      </div>

      {error && <p className="fawkes-error">{error}</p>}

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("instellingen.grootboek.loadFailed")}</p>
      ) : (
        <div className="grootboek-table-scroll">
          <IndeelbareTabel tabelKey="settings-grootboek-list"
            columns={columns}
            rows={rows}
            getRowKey={(g) => String(g.id)}
            empty={t("instellingen.grootboek.empty")}
          />
        </div>
      )}

      {/* Create / edit dialog */}
      <Modal
        open={edit != null}
        title={edit?.id == null ? t("instellingen.grootboek.create.title") : t("instellingen.grootboek.edit.title")}
        onClose={() => setEdit(null)}
        footer={
          edit && (
            <>
              <Button variant="secondary" onClick={() => setEdit(null)} disabled={isSaving}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={save} disabled={isSaving || !isEditValid}>
                {t("common.save")}
              </Button>
            </>
          )
        }
      >
        {edit && (
          <div className="grootboek-form">
            <Field
              label={t("instellingen.grootboek.field.nummer")}
              value={edit.rekeningNummer}
              onChange={(e) => setEdit({ ...edit, rekeningNummer: e.target.value })}
              required
            />
            <Field
              label={t("instellingen.grootboek.field.omschrijving")}
              value={edit.omschrijving}
              onChange={(e) => setEdit({ ...edit, omschrijving: e.target.value })}
              required
            />
            <SelectField
              label={t("instellingen.grootboek.field.type")}
              options={typeOptions}
              value={edit.rekeningType}
              onChange={(e) => setEdit({ ...edit, rekeningType: e.target.value })}
            />
            <CheckboxField
              label={t("instellingen.grootboek.field.actief")}
              checked={edit.actief}
              onChange={(e) => setEdit({ ...edit, actief: e.target.checked })}
            />
          </div>
        )}
      </Modal>

      {/* Delete-with-reassign dialog */}
      <Modal
        open={del != null}
        title={t("instellingen.grootboek.delete.title")}
        onClose={() => setDel(null)}
        footer={
          del && (
            <>
              <Button variant="secondary" onClick={() => setDel(null)} disabled={isSaving}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={isSaving || del.replacementId === ""}
                onClick={() => doDelete(del.row.id, del.replacementId)}
              >
                {t("instellingen.grootboek.delete.transferAndDelete")}
              </Button>
            </>
          )
        }
      >
        {del && (
          <div data-testid="grootboek-delete-dialog">
            <p className="fawkes-error">
              {t("instellingen.grootboek.delete.inUse", { args: [del.usage, del.row.rekeningNummer] })}
            </p>
            <SelectField
              label={t("instellingen.grootboek.delete.replacementLabel")}
              options={replacementOptions}
              value={del.replacementId}
              onChange={(e) => setDel({ ...del, replacementId: e.target.value })}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
